/**
 * Helpers for weight and dimension units configured in Settings.
 *
 * Values are stored in the unit they were entered in; conversions go through the
 * unit's factor to the base unit (grams for weight, millimetres for dimensions).
 */

export interface WeightUnitFactor {
  factor_to_g: number;
}

export interface DimensionUnitFactor {
  factor_to_mm: number;
}

type MaybeNumber = number | string | null | undefined;

const EMPTY = '—';

const toNumber = (value: MaybeNumber): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Format a number without trailing zeros, rounded to at most three decimal places. */
const formatNumber = (value: number): string => String(Number(value.toFixed(3)));

/** Convert a weight between two units (via grams). */
export const convertWeight = (value: number, from: WeightUnitFactor, to: WeightUnitFactor): number =>
  (value * from.factor_to_g) / to.factor_to_g;

/** Convert a length between two units (via millimetres). */
export const convertDimension = (value: number, from: DimensionUnitFactor, to: DimensionUnitFactor): number =>
  (value * from.factor_to_mm) / to.factor_to_mm;

/** "12.5 kg"; an empty value is rendered as "—". */
export const formatWeight = (value: MaybeNumber, symbol?: string | null): string => {
  const number = toNumber(value);
  if (number === null) return EMPTY;
  return symbol ? `${formatNumber(number)} ${symbol}` : formatNumber(number);
};

/** "120 × 60 × 80 cm"; missing sides are rendered as "—", all missing as a single "—". */
export const formatDimensions = (
  length: MaybeNumber,
  width: MaybeNumber,
  height: MaybeNumber,
  symbol?: string | null,
): string => {
  const values = [length, width, height].map(toNumber);
  if (values.every((value) => value === null)) return EMPTY;
  const text = values.map((value) => (value === null ? EMPTY : formatNumber(value))).join(' × ');
  return symbol ? `${text} ${symbol}` : text;
};
